// Copyright 2016 Sorama B.V.

import { Weighting } from "units/Weighting";

// Custom converter interface for cross-unit-family conversions
interface CustomConverter {
    (sourceUnit: Unit, targetUnit: Unit, value: number, context?: ConversionContext): number;
}

export interface ConversionContext {
    gasType?: string;
}

export abstract class UnitType {
    public static typeName: string;
}

export class UnitValue {
    public value: number;
    private _unit: Unit;

    constructor(value: number, unit: Unit) {
        this.value = value;
        this._unit = unit;
    }

    public get unit(): Unit {
        return this._unit;
    }

    public set unit(newUnit: Unit) {
        this.value = this.toUnit(newUnit);
        this._unit = newUnit;
    }

    public toBaseUnit(): number {
        return this.unit.toBaseUnit(this.value);
    }

    public toUnit(otherUnit: Unit, context?: ConversionContext): number {
        return otherUnit.fromUnit(this.unit, this.value, context);
    }
}

export class Unit {
    // Static registry for custom converters between unit families
    private static customConverters = new Map<string, CustomConverter>();

    public static registerCustomConverter(fromFamily: string, toFamily: string, converter: CustomConverter): void {
        const key = `${fromFamily}→${toFamily}`;
        this.customConverters.set(key, converter);
    }

    protected _unit: string;
    public get unit(): string {
        return this._unit;
    }

    protected _aliases: string[];
    public get aliases(): string[] {
        return this._aliases;
    }

    protected _conversion: number;
    public get conversion(): number {
        return this._conversion;
    }

    protected _unitType: typeof UnitType;
    public get unitType(): typeof UnitType {
        return this._unitType;
    }

    protected _unitFamily: typeof UnitType;
    public get unitFamily(): typeof UnitType {
        return this._unitFamily;
    }

    public isLinearUnit = true;

    public isGeneric = false;

    constructor(
        unit: string,
        conversion: number,
        unitType: typeof UnitType,
        unitOptions?: { unitFamily?: typeof UnitType; aliases?: string[] },
        isGeneric = false
    ) {
        this._unit = unit;
        this._conversion = conversion;

        this._unitType = unitType;
        this._unitFamily = unitOptions?.unitFamily ? unitOptions.unitFamily : unitType;

        this.isGeneric = isGeneric;

        // Concatenate main unit name and aliases
        this._aliases = [unit];
        if (unitOptions?.aliases) {
            this._aliases = this._aliases.concat(unitOptions.aliases);
        }
    }

    public toUnitType(): string {
        return this._unitFamily.typeName;
    }

    public isOfSameUnitFamily(other: Unit): boolean {
        return other._unitFamily === this._unitFamily || other.isGeneric || this.isGeneric;
    }

    public toBaseUnit(value: number): number {
        return value * this.conversion;
    }

    public fromUnit(unit: Unit, value: number, context?: ConversionContext): number {
        if (unit._unitFamily !== this._unitFamily && !unit.isGeneric && !this.isGeneric) {
            // Check for custom converters before throwing error
            const key = `${unit._unitFamily.typeName}→${this._unitFamily.typeName}`;
            const converter = Unit.customConverters.get(key);
            if (converter) {
                return converter(unit, this, value, context);
            }

            // Check reverse direction
            const reverseKey = `${this._unitFamily.typeName}→${unit._unitFamily.typeName}`;
            const reverseConverter = Unit.customConverters.get(reverseKey);
            if (reverseConverter) {
                // For reverse conversion, we need to reverse the operation
                return 1 / reverseConverter(this, unit, 1 / value, context);
            }

            throw new Error("Cannot convert from " + unit._unitFamily.typeName + " to " + this._unitFamily.typeName);
        }

        // Handle case where unit is a plain object without prototype methods (e.g., from JSON deserialization)
        if (typeof unit.toBaseUnit === "function") {
            value = unit.toBaseUnit(value);
        } else {
            // Fallback: use conversion factor directly (linear unit behavior)
            value = value * (unit.conversion ?? 1);
        }

        return value / this.conversion;
    }

    public toHumanReadableUnit(value: number): UnitValue {
        const unitNames = Object.keys(this._unitType);
        const units: Unit[] = unitNames
            .map((x) => <Unit>this._unitType[x])
            .filter((x) => !!x.fromUnit && this.isLinearUnit == x.isLinearUnit) // Only grab properties of type Unit with matching linearity
            .sort((a, b) => a.conversion - b.conversion);

        // Choose best one
        let bestUnit: Unit = units[0];
        let bestUnitValue: number = units[0].fromUnit(this, value);
        const bestUnitValueDigits: number = bestUnitValue.toFixed(0).length;

        for (const currentUnit of units) {
            const unitValue = currentUnit.fromUnit(this, value);
            const unitValueDigits = unitValue.toFixed(0).length;

            // Best Human-Readable unit is the one with 1, 2 or 3 digits before the decimal point
            if (unitValueDigits >= 1 && unitValueDigits <= 3) {
                bestUnitValue = unitValue;
                bestUnit = currentUnit;
                break;
            } else if (unitValueDigits < bestUnitValueDigits) {
                // Backup: just take the one with the least digits
                bestUnitValue = unitValue;
                bestUnit = currentUnit;
            }
        }

        return new UnitValue(bestUnitValue, bestUnit);
    }

    /**
     * Get a list of named units from a class with static Unit objects
     * @param unitClass a class with static Unit objects
     * @returns an array of NamedUnit objects
     */
    public static getNamedList(unitClass: unknown): NamedUnit[] {
        return Object.keys(unitClass)
            .map((c) => {
                const namedUnit = unitClass[c];
                if (namedUnit instanceof Unit) {
                    namedUnit["name"] = c;
                    return namedUnit;
                }
                return null;
            })
            .filter((c) => c != null)
            .map((c) => <NamedUnit>c);
    }
}

export class MultiUnitValue {
    public value: number;
    private _units: Unit[];

    constructor(value: number, units: Unit[]) {
        this.value = value;
        this._units = units;
    }

    public get units(): Unit[] {
        return this._units;
    }

    public set units(newUnits: Unit[]) {
        this.value = this.toUnits(newUnits);
        this._units = newUnits;
    }

    public toBaseUnits(): number {
        return this._units.reduce((acc, current) => current.toBaseUnit(acc), this.value);
    }

    public toUnits(otherUnits: Unit[]): number {
        if (otherUnits.length != this._units.length) {
            throw new Error("Cannot convert between different amount of units");
        }

        const value = this.value;
        for (let i = 0; i < this._units.length; i++) {
            otherUnits[0].fromUnit(this._units[i], value);
        }

        return value;
    }
}
export class NamedUnit extends Unit {
    public name: string;
}

export class LogarithmicUnit extends Unit {
    private logBase: number;

    public readonly isLinearUnit = false;

    constructor(
        unit: string,
        logBase: number,
        conversion: number,
        unitType: typeof UnitType,
        unitOptions?: { unitFamily?: typeof UnitType; aliases?: string[] },
        isGeneric = false
    ) {
        super(unit, conversion, unitType, unitOptions, isGeneric);
        this.logBase = logBase;
    }

    public toBaseUnit(value: number): number {
        return super.toBaseUnit(Math.pow(this.logBase, value));
    }

    public fromUnit(unit: Unit, value: number): number {
        const converted = super.fromUnit(unit, value);
        return Math.log(converted) / Math.log(this.logBase);
    }
}
export class WeightedUnit extends Unit {
    protected _weighting: Weighting;
    public get weighting(): Weighting {
        return this._weighting;
    }

    constructor(
        weightingPrefixUnit: string,
        weightingPostfixUnit: string,
        weighting: Weighting,
        conversion: number,
        unitFamily?: typeof UnitType,
        isGeneric = false
    ) {
        super(
            weightingPrefixUnit + weighting.name + weightingPostfixUnit,
            conversion,
            unitFamily,
            undefined,
            isGeneric
        );
        this._weighting = weighting;
    }
}

export class dBUnit extends WeightedUnit {
    private dbRefValue: number;
    private dbScaleValue: number;

    public readonly isLinearUnit = false;

    constructor(
        weightingPrefixUnit: string,
        weightingPostfixUnit: string,
        weighting: Weighting,
        dbRefValue: number,
        dbScaleValue: number,
        conversion: number,
        unitFamily: typeof UnitType,
        isGeneric = false
    ) {
        super(weightingPrefixUnit, weightingPostfixUnit, weighting, conversion, unitFamily, isGeneric);
        this.dbRefValue = dbRefValue;
        this.dbScaleValue = dbScaleValue;
    }

    public toBaseUnit(value: number): number {
        return super.toBaseUnit(Math.pow(10, value / this.dbScaleValue) * this.dbRefValue);
    }

    public fromUnit(unit: Unit, value: number): number {
        const converted = super.fromUnit(unit, value);

        // To db
        return this.dbScaleValue * Math.log10(converted / this.dbRefValue);
    }
}

/**
 * A special type of unit representing critical bands and bark bands
 * Allows conversion to and from frequency in Hz
 */
export class CriticalBandUnit extends Unit {
    constructor(
        unit: string,
        public convertBandToHz: (band: number) => number,
        public convertHzToBand: (frequency: number) => number,
        public convertSubBandToBand: (subBandIndex: number) => number,
        unitType: typeof UnitType,
        unitOptions?: { unitFamily?: typeof UnitType; aliases?: string[] },
        isGeneric = false
    ) {
        super(unit, 1, unitType, unitOptions, isGeneric);
    }

    public fromUnit(unit: Unit, value: number): number {
        // If it's the same unit, return the value
        if (unit.unit === this.unit) {
            return value;
        }

        if (unit.unitType.typeName === "Frequency") {
            return this.convertHzToBand(value);
        }

        // If converting from one critical band to another, convert via frequency
        if (unit.isOfSameUnitFamily(this) && unit instanceof CriticalBandUnit) {
            const freq = unit.convertBandToHz(value);
            return this.convertHzToBand(freq);
        }

        if (unit.isGeneric) {
            return value;
        }

        throw new Error("Cannot convert from non-frequency unit to critical band");
    }
}
